/**
 * Tela de criação de nova senha a partir do link de e-mail (E35.F2). // ux-neutrality:allow doc-comment
 *
 * Lê o parâmetro do link (`?token=`) da URL, coleta a nova senha + confirmação // ux-neutrality:allow doc-comment
 * e chama POST /api/auth/reset-password. Link ausente/inválido/expirado mostra // ux-neutrality:allow doc-comment
 * erro com CTA para solicitar um novo. Sucesso redireciona para /login com aviso. // ux-neutrality:allow doc-comment
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

import { Blueprint, Button, Field, Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { resetPassword, ResetPasswordError } from '@/lib/auth';
import { senhaForte } from '@/lib/senha_forte';

export function ResetPasswordPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const t = ptBR.resetPassword;
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token') ?? ''; // ux-neutrality:allow auth-token (param do link, RFC 7519)

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setError(null);
    if (!resetToken) {
      setError(t.missingLink);
      return;
    }
    if (!senhaForte(password)) {
      setError(t.tooShort);
      return;
    }
    if (password !== confirm) {
      setError(t.mismatch);
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(apiClient, resetToken, password);
      // E35.F2.C03 (F-E35.F2-03): redirect para /login com aviso (em vez de
      // tela de sucesso com CTA manual), conforme escopo de T06.
      navigate('/login', { replace: true, state: { passwordReset: true } });
    } catch (err) {
      const reason = err instanceof ResetPasswordError ? err.reason : 'unknown';
      if (reason === 'invalid_token') {
        setError(t.errorInvalidLink);
      } else if (reason === 'weak_password') {
        setError(t.errorWeak);
      } else if (reason === 'too_many_attempts') {
        setError(t.errorTooMany);
      } else if (reason === 'network') {
        setError(t.errorNetwork);
      } else {
        setError(ptBR.errors.generic);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Blueprint
      as="section"
      data-testid="reset-password-page"
      aria-labelledby="reset-title"
      className="bg-bg px-8 py-[34px]"
    >
      <p className="mb-2.5 font-titulo text-rotulo uppercase text-fg-dim">
        {ptBR.authShell.accessKicker}
      </p>
      <h1 id="reset-title" className="font-display text-[32px] leading-none text-fg">
        {t.pageTitle}
      </h1>

      <p className="mt-1.5 text-body-sm text-fg-muted">{t.intro}</p>
      <form onSubmit={handleSubmit} className="mt-[26px] flex flex-col gap-4">
        <Field
          label={t.passwordLabel}
          htmlFor="reset-password-input"
          description={ptBR.senhaForte.regra}
        >
          <div className="relative flex">
            <Input
              id="reset-password-input"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.passwordPlaceholder}
              disabled={submitting}
              className="pr-10"
              data-testid="reset-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? t.passwordHide : t.passwordShow}
              aria-pressed={showPassword}
              data-testid="reset-password-toggle"
              className="absolute inset-y-0 right-2 grid w-7 place-items-center text-fg-dim transition-colors duration-fast ease-sc hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
            >
              {showPassword ? (
                <EyeOff className="h-[18px] w-[18px]" aria-hidden="true" />
              ) : (
                <Eye className="h-[18px] w-[18px]" aria-hidden="true" />
              )}
            </button>
          </div>
        </Field>

        <Field label={t.confirmLabel} htmlFor="reset-confirm-input">
          <Input
            id="reset-confirm-input"
            type={showPassword ? 'text' : 'password'}
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t.confirmPlaceholder}
            disabled={submitting}
            data-testid="reset-confirm"
          />
        </Field>

        {error ? (
          <div
            role="alert"
            data-testid="reset-error"
            className="flex flex-col gap-2 border border-danger-500 bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
          >
            <span>{error}</span>
            <Link
              to="/login"
              className="self-start text-accent-700 underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-accent"
              data-testid="reset-request-new"
            >
              {t.requestNew}
            </Link>
          </div>
        ) : null}

        <Button
          type="submit"
          size="block"
          disabled={submitting}
          className="mt-1.5 tracking-[0.04em]"
          data-testid="reset-submit"
        >
          {submitting ? t.submitting : t.submit}
        </Button>
      </form>
    </Blueprint>
  );
}
